/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, { IDictionary, IRequestContext } from '@msdyn365-commerce/core';
import { ProductBehavior } from '@msdyn365-commerce/retail-proxy';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { debounce } from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';
import { DEFAULT_MAX_QUANTITY } from '../../../../utilities/quantity-constants';

export enum VariantType {
    Browser,
    Viewport
}

/**
 * Returns the payload Object.
 * @param {string} contentCategory - Content Category.
 * @param {IModule} props - Module props.
 * @param {string} moduleName - Module Name.
 * @param eventType
 * @param telemetryContent
 * @param {string} etext - Etext Name.
 * @param {string} recid - Recid Name.
 */
export function getPayloadObject(eventType: string, telemetryContent: ITelemetryContent, etext: string, recid?: string): IPayLoad {
    const pageName = telemetryContent && telemetryContent.pageName ? telemetryContent.pageName : '';
    const moduleName = telemetryContent && telemetryContent.moduleName ? telemetryContent.moduleName : '';
    const payload: IPayLoad = {
        contentCategory: eventType,
        contentAction: {
            pgname: pageName,
            mname: moduleName,
            etext,
            recid,
            etype: eventType
        }
    };
    return payload;
}

export interface IMobileProps {
    variant?: VariantType;
    context?: IRequestContext;
}

export const isMobile = (props: IMobileProps) => {
    const { variant, context } = props;
    switch (variant) {
        case VariantType.Browser:
            if (MsDyn365.isBrowser && window.innerWidth) {
                const gridSettings = context?.gridSettings;
                if (gridSettings) {
                    if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                        return 'xs';
                    } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                        return 'sm';
                    } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                        return 'md';
                    } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                        return 'lg';
                    }
                    return 'xl';
                }
            }

            return 'lg';
        case VariantType.Viewport:
            const viewport = context?.device?.Type === 'Mobile' ? 'xs' : 'lg';
            return viewport;
        default:
            return 'lg';
    }
};

/**
 * Returns the telemetry Attributes.
 * @param {IModule} props - Module Props.
 * @param telemetryContent
 * @param {IPayLoad} payLoad - Pay Load.
 */
export function getTelemetryAttributes(telemetryContent: ITelemetryContent, payLoad: IPayLoad): IDictionary<string> | undefined {
    const contentData = payLoad.contentAction.recid ? payLoad.contentAction.recid : payLoad.contentAction.etext;
    const attributes: IDictionary<string> | undefined =
        telemetryContent &&
        telemetryContent.telemetry &&
        telemetryContent.telemetry.setTelemetryAttribute &&
        telemetryContent.telemetry.setTelemetryAttribute(contentData!, payLoad.contentAction);
    return attributes;
}

export interface IContentAction {
    pgname: string;
    mname: string;
    etext?: string;
    recid?: string;
    etype?: string;
}

export interface IPayLoad {
    contentCategory: string;
    contentAction: IContentAction;
}

export enum TelemetryConstant {
    ShopSimliarLooks = 'shop similar looks',
    ShopSimliarDescription = 'shop similar description',
    PickupInStore = 'pick up in store',
    PreferredStore = 'set as preferred store',
    RemovePreferredStore = 'remove preferred store',
    IncrementQuantity = 'Increment quantity',
    DecrementQuantity = 'Decrement quantity',
    Checkout = 'Checkout',
    GuestCheckout = 'Guest checkout',
    BackToShopping = 'Back to shopping',
    ApplyPromoCode = 'Apply promo code',
    ViewMore = 'View more',
    OrderDetails = 'Order details',
    AvailablePoints = 'Available points',
    ExpiringPoints = 'Expiring points',
    AddUser = 'Add user',
    BuyItAgain = 'Buy it again',
    ContactNumber = 'ContactNumber',
    CheckoutChange = 'Change',
    Save = 'Save',
    SaveContinue = 'Save and Continue',
    Cancel = 'Cancel',
    DeliveryOptions = 'Delivery options',
    CheckoutTermsAndConditions = 'Checkout terms and conditions',
    ExpandAll = 'Expand all',
    CollapseAll = 'Collapse all',
    Expand = 'Expand',
    Collapse = 'Collapse',
    SignIn = 'Sign in',
    SignUp = 'Sign up',
    SignOut = 'Sign out',
    SwitchCustomer = 'Switch Customer',
    LoyaltyDetailLink = 'Loyalty detail ink',
    LoyaltyTermsLink = 'Loyalty terms link',
    LoyaltyTermsCancel = 'Loyalty terms cancel',
    LoyaltyTermsSubmit = 'Loyalty terms submit',
    LoyaltyTermsAgree = 'Loyalty terms agree',
    RemoveCartItem = 'Remove cart item',
    EditCart = 'Edit cart',
    BackToTop = 'Back to top',
    Logo = 'Logo',
    NavIcon = 'Navigation icon',
    ViewBagAndCheckout = 'View bag and checkout',
    ContinueShopping = 'Continue shopping',
    AddOrderTemplateToCart = 'Add order template to cart',
    PromoBannerClose = 'Close promo banner',
    WriteReview = 'Write Review',
    Next = 'Next',
    Previous = 'Previous',
    SubmitReview = 'Submit review',
    SuccessButton = 'Success',
    SearchStore = 'Search store',
    ShowAllStore = 'Show all store',
    ToggleMapView = 'Toggle map view',
    PrivacyPolicy = 'Privacy policy',
    ViewShoppingBag = 'View shopping bag',
    MyProfile = 'My profile',
    seeDetails = 'See Details',
    CurrentDistributor = 'Set as current distributor',
    ViewMode = 'View mode',
    ApplyRefiner = 'Apply Refiner',
    Search = 'Search'
}

interface ITelemetryContent {
    pageName: string;
    moduleName: string;
    telemetry?: ITelemetry;
}

interface IIncrementalQuantityState {
    isUpdating: boolean;
    currentInput: string;
}

interface IIncrementalQuantityProps {
    min?: number;
    max: number;
    multipleOfQuantity: number;
    currentCount?: number;
    disabled?: boolean;
    incrementGlyphClass?: string;
    decrementGlyphClass?: string;
    decrementButtonAriaLabel?: string;
    incrementButtonAriaLabel?: string;
    minQuantityText: string;
    maxQuantityText: string;
    inputQuantityAriaLabel?: string;
    isGiftCard?: boolean;
    id?: string;
    telemetryContent?: ITelemetryContent;
    applyDefaultOrderSettings?: boolean;
    onChange?(newValue: number): boolean | void;
    productBehavior: ProductBehavior;
    reset?: boolean;
    handleReset?: () => void;
}
/**
 * Quantity Component - This component is used to add or remove quantity.
 */
@observer
export default class IncrementalQuantity extends React.PureComponent<IIncrementalQuantityProps, IIncrementalQuantityState> {
    public static defaultProps: Partial<IIncrementalQuantityProps> = {
        min: 1,
        max: DEFAULT_MAX_QUANTITY,
        multipleOfQuantity: 0.01,
        decrementGlyphClass: 'fas fa-minus',
        incrementGlyphClass: 'fas fa-plus'
    };

    private _minValue: number;
    private _maxValue: number;
    private _multipleQuantityValue: number;

    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private readonly payLoad: IPayLoad;

    public static getDerivedStateFromProps(
        props: Readonly<IIncrementalQuantityProps>,
        state: IIncrementalQuantityState
    ): IIncrementalQuantityState {
        const nextState = { ...state };

        if (props.disabled && !state.isUpdating) {
            nextState.isUpdating = true;
        }

        let updatedValue = parseFloat(state.currentInput);

        if (
            (!props.disabled && state.isUpdating) ||
            ((props.applyDefaultOrderSettings ?? false) &&
                updatedValue === 1.0 &&
                props.currentCount &&
                props.currentCount > IncrementalQuantity.defaultProps.min!)
        ) {
            updatedValue = props.currentCount ?? 1.0;
            nextState.isUpdating = false;
        }

        return nextState;
    }

    constructor(props: IIncrementalQuantityProps) {
        super(props);
        let minQuantity = IncrementalQuantity.defaultProps.min!;
        let maxQuantity = IncrementalQuantity.defaultProps.max!;
        let multipleOfQuantity = IncrementalQuantity.defaultProps.multipleOfQuantity!;

        if (props.productBehavior) {
            minQuantity = props.productBehavior.MinimumQuantity ?? IncrementalQuantity.defaultProps.min!;
            multipleOfQuantity = props.productBehavior.MultipleOfQuantity ?? IncrementalQuantity.defaultProps.multipleOfQuantity!;
        }

        // Read MaximumQuantity from productBehavior if available, then from props.max, then DEFAULT_MAX_QUANTITY
        if (props.productBehavior?.MaximumQuantity && props.productBehavior.MaximumQuantity > 0) {
            maxQuantity = props.productBehavior.MaximumQuantity;
        } else if (props.max && props.max > 0) {
            maxQuantity = props.max;
        }

        this.state = {
            isUpdating: false,
            currentInput: props.productBehavior
                ? minQuantity > 0
                    ? minQuantity.toFixed(2)
                    : multipleOfQuantity > 0
                    ? multipleOfQuantity.toFixed(2)
                    : '1.00'
                : props.currentCount?.toFixed(2) ?? '1.00'
        };
        this._onIncrement = this._onIncrement.bind(this);
        this._onDecrement = this._onDecrement.bind(this);
        this._handleChange = this._handleChange.bind(this);
        this.payLoad = getPayloadObject('click', this.props.telemetryContent!, '');
        this._minValue = minQuantity ?? IncrementalQuantity.defaultProps.min!;
        this._maxValue = maxQuantity ?? IncrementalQuantity.defaultProps.max!;

        this._multipleQuantityValue = multipleOfQuantity ?? IncrementalQuantity.defaultProps.multipleOfQuantity!;
    }

    public render(): JSX.Element {
        const currentInput = this.state.currentInput;
        const glyphMinusClassName: string = `${this.props.decrementGlyphClass!} quantity__controls-glyph`;
        const glyphPlusClassName: string = `${this.props.incrementGlyphClass!} quantity__controls-glyph`;
        const step = this._multipleQuantityValue > 0 ? this._multipleQuantityValue : 1;
        const nextDecrementValue = parseFloat(currentInput) - step;
        const decrementDisabled = parseFloat(currentInput) <= this._minValue || this.props.isGiftCard || nextDecrementValue < this._minValue;
        const incrementDisabled = parseFloat(currentInput) + step > Number(this._maxValue) || this.props.isGiftCard;
        const currentValue = parseFloat(currentInput);
        this.payLoad.contentAction.etext = TelemetryConstant.DecrementQuantity;
        const decrementAttributes = getTelemetryAttributes(this.props.telemetryContent!, this.payLoad);
        this.payLoad.contentAction.etext = TelemetryConstant.IncrementQuantity;
        const incrementAttributes = getTelemetryAttributes(this.props.telemetryContent!, this.payLoad);
        const ariaLablelText = `${this.props.inputQuantityAriaLabel ?? ''} ${parseFloat(currentInput)}`;

        if (this.props.reset === true) {
            this.setState({ currentInput: '1.00' });
            if (this.props.handleReset) {
                this.props.handleReset();
            }
        }

        let extraClassDecrement = '';
        if (decrementDisabled) {
            // The quantity has reached its boundaries (max or min)
            extraClassDecrement = 'disabled';
        } else if (this.props.disabled) {
            // This.props.disabled shows if the state is not succeded yet
            extraClassDecrement = 'transition';
        }
        let extraClassIncrement = '';
        if (incrementDisabled) {
            // The quantity has reached its boundaries (max or min)
            extraClassIncrement = 'disabled';
        } else if (this.props.disabled) {
            // This.props.disabled shows if the state is not succeded yet
            extraClassIncrement = 'transition';
        }

        return (
            <>
                <div className='quantity' id={this.props.id}>
                    <button
                        disabled={this.props.disabled || decrementDisabled}
                        title={decrementDisabled ? '' : this.props.decrementButtonAriaLabel}
                        className={`decrement quantity__controls ${extraClassDecrement}`}
                        onClick={this._onDecrement}
                        aria-hidden={!!decrementDisabled}
                        aria-label={`${this.props.decrementButtonAriaLabel}`}
                        color='secondary'
                        {...decrementAttributes}
                    >
                        <span className={glyphMinusClassName} />
                    </button>
                    <input
                        type='text'
                        className='quantity-input'
                        value={currentInput}
                        onChange={this._handleChange}
                        onBlur={this._validateMin}
                        aria-live='polite'
                        aria-label={this.isMobileView() ? ariaLablelText : `${this.props.inputQuantityAriaLabel ?? ''}`}
                        role='spinbutton'
                        aria-valuemin={this._minValue}
                        aria-valuemax={this._maxValue}
                        aria-valuenow={currentValue}
                        ref={this.inputRef}
                        disabled={this.props.disabled}
                        min={this._minValue}
                        max={this._maxValue}
                    />
                    <button
                        disabled={this.props.disabled || incrementDisabled}
                        title={incrementDisabled ? '' : this.props.incrementButtonAriaLabel}
                        className={`increment quantity__controls ${extraClassIncrement}`}
                        onClick={this._onIncrement}
                        aria-hidden={!!incrementDisabled}
                        aria-label={`${this.props.incrementButtonAriaLabel}`}
                        color='secondary'
                        {...incrementAttributes}
                    >
                        <span className={glyphPlusClassName} />
                    </button>
                </div>
            </>
        );
    }

    /**
     * It checks if its in mobile view or not.
     * @returns Boolean.
     */
    private readonly isMobileView = (): boolean => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Added gridsetting values.
        const context = {
            gridSettings: {
                xs: { w: 767 },
                sm: { w: 991 },
                md: { w: 1199 },
                lg: { w: 1599 },
                xl: { w: 1600 }
            }
        } as IRequestContext;

        const isMobileCheck = isMobile({ variant: VariantType.Browser, context });
        return isMobileCheck === 'xs' || isMobileCheck === 'sm';
    };

    private _onIncrement(): void {
        const currQuantity = parseFloat(this.state.currentInput);
        const step = this._multipleQuantityValue > 0 ? this._multipleQuantityValue : 1;

        // Jump to the next valid multiple above current value
        let updatedQuantity = Math.ceil((currQuantity + Number.EPSILON) / step) * step;
        if (updatedQuantity <= currQuantity) {
            updatedQuantity = currQuantity + step;
        }
        // Round to avoid floating point drift
        updatedQuantity = Math.round(updatedQuantity / step) * step;

        if (updatedQuantity > this._maxValue) {
            return;
        }

        if (this.props.onChange) {
            if (this.props.onChange(updatedQuantity)) {
                this.setState({ currentInput: updatedQuantity.toFixed(2) });
            }
        }
    }

    private _onDecrement(): void {
        const currQuantity = parseFloat(this.state.currentInput);
        const step = this._multipleQuantityValue > 0 ? this._multipleQuantityValue : 1;

        // Jump to the next valid multiple below current value
        let updatedQuantity = Math.floor((currQuantity - Number.EPSILON) / step) * step;
        if (updatedQuantity >= currQuantity) {
            updatedQuantity = currQuantity - step;
        }
        // Round to avoid floating point drift
        updatedQuantity = Math.round(updatedQuantity / step) * step;

        if (updatedQuantity < this._minValue) {
            return;
        }

        if (this.props.onChange) {
            if (this.props.onChange(updatedQuantity)) {
                this.setState({ currentInput: updatedQuantity.toFixed(2) });
            }
        }
    }

    public componentDidUpdate(prevProps: IIncrementalQuantityProps): void {
        if (prevProps.productBehavior !== this.props.productBehavior) {
            const behavior = this.props.productBehavior;
            const minQty = behavior?.MinimumQuantity ?? IncrementalQuantity.defaultProps.min!;
            const multipleQty = behavior?.MultipleOfQuantity ?? IncrementalQuantity.defaultProps.multipleOfQuantity!;
            const maxQty =
                behavior?.MaximumQuantity && behavior.MaximumQuantity > 0
                    ? behavior.MaximumQuantity
                    : this.props.max > 0
                    ? this.props.max
                    : IncrementalQuantity.defaultProps.max!;

            this._minValue = minQty;
            this._maxValue = maxQty;
            this._multipleQuantityValue = multipleQty;

            // If a currentCount (e.g. existing cart line quantity) is provided, use it — don't reset to min.
            // Only fall back to minQuantity when there is no existing quantity to preserve.
            const step = multipleQty > 0 ? multipleQty : 1;
            if (this.props.currentCount && this.props.currentCount > 0) {
                this.setState({ currentInput: this.props.currentCount.toFixed(2) });
            } else {
                let initialValue = minQty > 0 ? minQty : step;
                if (step > 0 && initialValue % step !== 0) {
                    initialValue = Math.ceil(initialValue / step) * step;
                }
                this.setState({ currentInput: initialValue.toFixed(2) });
                if (this.props.onChange) {
                    this.props.onChange(initialValue);
                }
            }
        }
    }

    private _handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
        let value = e.target.value;

        // If the input field is empty, we update the state to an empty field.
        if (value === '') {
            this.setState({ currentInput: '' });
        } else if (/^[0-9]*[.]?[0-9]{0,2}$/.test(value)) {
            // Converts the comma to a dot and stores the valid value in the state.
            const formattedValue = value.replace(',', '.');
            this.setState({ currentInput: formattedValue });

            debounce(() => {
                this.props.onChange && this.props.onChange(parseFloat(this.state.currentInput));

                const inputElement =
                    this.inputRef && this.inputRef.current && this.inputRef.current instanceof HTMLInputElement && this.inputRef.current;
                if (inputElement) {
                    inputElement.setAttribute('aria-valuenow', this.state.currentInput);
                    inputElement.setAttribute('value', this.state.currentInput);
                }
            }, 1000)();
        }
    }

    /**
     * Validates min and updates.
     */
    private readonly _validateMin = (): void => {
        const step = this._multipleQuantityValue > 0 ? this._multipleQuantityValue : 1;

        // If the input field is empty, reset to minimum valid value
        if (this.state.currentInput === '' || isNaN(parseFloat(this.state.currentInput))) {
            const initialValue = this._minValue > 0 ? this._minValue : step;
            this.setState({ currentInput: initialValue.toFixed(2) });
            this.props.onChange && this.props.onChange(initialValue);
            return;
        }

        const currentValue = parseFloat(this.state.currentInput);
        let validValue = Math.min(this._maxValue, Math.max(this._minValue, currentValue));

        // Snap to the nearest valid multiple
        if (step > 0 && Math.round(validValue * 1e10) % Math.round(step * 1e10) !== 0) {
            validValue = Math.ceil(validValue / step) * step;
            // Round to avoid floating point drift
            validValue = Math.round(validValue / step) * step;
        }

        // Clamp again after rounding
        validValue = Math.min(this._maxValue, Math.max(this._minValue, validValue));

        if (validValue !== currentValue) {
            this.setState({ currentInput: validValue.toFixed(2) });
            this.props.onChange && this.props.onChange(validValue);
        }
    };
}
